import React, { useEffect, useState } from "react";

// Semantic UI React
import { Menu, Icon, Segment, Sidebar, Button } from "semantic-ui-react";

// Styling
import "semantic-ui-css/semantic.min.css";
import "../Shared/Style.css";
import "./style.css";

// ESRI
import Map from "@arcgis/core/Map";
import MapView from "@arcgis/core/views/MapView";
import Extent from "@arcgis/core/geometry/Extent";
import esriId from "@arcgis/core/identity/IdentityManager";
import Zoom from "@arcgis/core/widgets/Zoom";
import TileLayer from "@arcgis/core/layers/TileLayer";
import * as query from "@arcgis/core/rest/query.js";
import Query from "@arcgis/core/rest/support/Query.js";
import * as reactiveUtils from "@arcgis/core/core/reactiveUtils.js";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer.js";
import SpatialReference from "@arcgis/core/geometry/SpatialReference.js";
import * as projection from "@arcgis/core/geometry/projection.js";
import PopupTemplate from "@arcgis/core/PopupTemplate.js";
import ActionButton from "@arcgis/core/support/actions/ActionButton.js";
import SimpleFillSymbol from "@arcgis/core/symbols/SimpleFillSymbol.js";
import Color from "@arcgis/core/Color.js";
import MapImageLayer from "@arcgis/core/layers/MapImageLayer";

// components
import ZoomWidget from "../GISMappingApp/MapComponents/ZoomWidget";
import LayersWidget from "./LayersWidget";
import SearchWidget from "../Shared/components/SearchWidget";
import useResizeHandler from "../Shared/useResizeHandler";

// Types
import { LayerObj } from "./Types";

const Vanguard = ({ config }) => {
  const [view, setView] = useState(null);
  const [visibleLayers, setVisibleLayers] = useState<LayerObj | null>();
  const { height } = useResizeHandler();
  const [tabHeight, setTabHeight] = useState(500);
  const [selectedTab, setSelectedTab] = useState("search");
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [initialExtent, setInitialExtent] = useState(
    config.default.extent
      ? new Extent(config.default.extent.extent).expand(1.5)
      : new Extent({
          xmin: 2170244.54269575,
          ymin: 257059.937828542,
          xmax: 2421112.5982513,
          ymax: 408622.43782854,
          spatialReference: {
            wkid: 3418,
          },
        })
  );

  // console.log("Vanguard", {
  //   token,
  //   config,
  //   initialExtent,
  //   tabHeight,
  // });

  useEffect(() => {
    const menu_height = document.getElementById("sidebar-menu").offsetHeight;
    setTabHeight(height - menu_height);
  }, [height]);

  useEffect(() => {
    // async function initialize() {
    // Load ESRI's IdentifyManager and add the token
    esriId.registerToken({
      server: config.server_url,
      token: config.token,
    });

    var basemap = new TileLayer({
      url: config.default.basemap_url,
      visible: true,
      id: "basemap",
      title: "basemap",
      minScale: 409600,
      maxScale: 200,
    });

    var sc = new TileLayer({
      url: config.default.sc_url,
      id: "Surrounding Counties",
      title: "Surrounding Counties",
      visible: true,
      minScale: 409600,
      maxScale: 200,
    });

    const road_labels = new TileLayer({
      url: config.default.road_labels_url,
      id: "Road Labels",
      title: "Road Labels",
      visible: true,
      minScale: 409600,
      maxScale: 200,
    });

    const parcel_lot_numbers = new TileLayer({
      url: config.default.lot_numbers_url,
      id: "Lot Numbers",
      title: "Lot Numbers",
      visible: false,
      minScale: 409600,
      maxScale: 200,
    });

    const e911_address_points = new TileLayer({
      url: config.default.e911_url,
      id: "E911",
      title: "E911",
      visible: true,
      minScale: 409600,
      maxScale: 200,
    });

    const historic_lot_lines = new MapImageLayer({
      url: config.default.lot_lines_url,
      id: "Historic Lot Lines",
      title: "Historic Lot Lines",
      visible: false,
      minScale: 409600,
      maxScale: 200,
    });

    const parcel_dimensions = new TileLayer({
      url: config.default.parcel_dimensions_url,
      id: "Parcel Demensions",
      title: "Parcel Demensions",
      visible: false,
      minScale: 409600,
      maxScale: 200,
    });

    const parcel_labels = new TileLayer({
      url: config.default.parcel_labels_url,
      id: "Parcel Labels",
      title: "Parcel Labels",
      visible: false,
      minScale: 409600,
      maxScale: 200,
    });

    const template = new PopupTemplate({
      // autocasts as new PopupTemplate()
      title: "Parcel: {PARCEL_NO}",
      actions: [
        new ActionButton({
          title: "Pictometry",
          id: "pictometry",
          icon: "launch",
        }),
      ],
      content: (feature) => {
        var attr = feature.graphic.attributes;
        // console.log({ feature, attr });

        return `
        <table class="esri-widget__table" summary="List of attributes and values">
          <tbody>
            <tr>
              <th style="width: 25%">Owner</th>
              <td>${attr.OwnerName}</td>
            </tr>
            <tr>
              <th style="width: 25%">Address</th>
              <td>${attr.FullSitus}</td>
            </tr>
          </tbody>
        </table>
        <div class="ui divider" style="margin: 0.5em 0"></div>
        <table summary="List of attributes and values" style="width: 100%;">
        <tbody>
          <tr>
            <td style="width: 50%">
              <a href="https://desmoines.iowaassessors.com/parcel.php?parcel=${attr.ParcelNumb}"} class="ui secondary tiny fluid button" style="background-color: #272727">
                <i class="external icon"></i>
                Assessor
              </a>
            </td>
            <td style="width: 50%">
              <a href="https://www.dmcgis.com/apps/gma?parcelid=${attr.PARCEL_NO}"} class="ui secondary tiny fluid button" style="background-color: #272727">
                <i class="external icon"></i>
                GIS Mapping App
              </a>
            </td>
          </tr>
          </tbody>
        </table>
    `;
      },
    });

    const parcels = new FeatureLayer({
      // URL to the service
      url: config.parcels_url,
      popupEnabled: true,
      popupTemplate: template,
      outFields: ["*"],
    });

    let map = new Map({
      layers: [
        basemap,
        sc,
        road_labels,
        parcel_lot_numbers,
        historic_lot_lines,
        parcels,
        parcel_dimensions,
        parcel_labels,
        e911_address_points,
      ],
    });

    let mapView = new MapView({
      container: "map-container",
      map: map,
      extent: initialExtent,
      popup: {
        dockEnabled: true,
        dockOptions: {
          buttonEnabled: false,
          breakpoint: false,
        },
      },
      constraints: {
        rotationEnabled: false,
        minScale: 409600,
        maxScale: 200,
        minZoom: 0,
        maxZoom: 11,
      },
    });
    mapView.popup.dockOptions = {
      buttonEnabled: false,
      // breakpoint: falsed,
    };
    mapView.popup.dockEnabled = false;
    mapView.popup.highlightEnabled = false;

    mapView.when(() => {
      new Zoom({
        view: mapView,
      });

      setVisibleLayers({
        road_labels,
        parcel_lot_numbers,
        e911_address_points,
        historic_lot_lines,
        parcel_dimensions,
        parcel_labels,
        parcels,
      });

      if (config.default.selected_parcel) {
        const query = new Query();
        query.where = `PARCEL_NO='${config.default.selected_parcel}' OR ParcelNumb='${config.default.selected_parcel}'`;
        query.returnGeometry = true;
        query.outFields = ["*"];

        parcels.queryFeatures(query).then(function (results) {
          // console.error(results.features); // prints the array of features to the console

          mapView.openPopup({
            features: [results.features[0]],
          });
        });
      }

      setView(mapView);
    });

    reactiveUtils.watch(
      () => mapView.popup.selectedFeature,
      (graphic) => {
        // console.log({ graphic, mapView });

        if (graphic) {
          var selected_graphic = graphic.clone();

          mapView.graphics.removeAll();

          selected_graphic.symbol = new SimpleFillSymbol({
            outline: {
              width: 3,
              color: [255, 0, 0, 0.8],
              cap: "round",
              join: "round",
            },
            color: new Color([26, 26, 26, 0.01]),
          });

          mapView.graphics.add(selected_graphic);
        }
      }
    );

    // Watch for the trigger-action event on the popup
    reactiveUtils.on(
      () => mapView.popup,
      "trigger-action",
      (event) => {
        projection.load().then(function () {
          const result = projection.project(
            mapView.popup.location,
            new SpatialReference({
              wkid: 4326,
            })
          );

          window.open(
            // @ts-ignore
            `https://www.dmcwebgis.com/apps/pictometry/?x=${result.longitude}&y=${result.latitude}`
          );
        });
      }
    );
  }, []);

  const searchCallback = (record) => {
    // console.log("searchCallback", { record });

    if (!view) {
      return false;
    }

    // Check if the width of the app is less than 800 and if so then close the sidebar
    if (window.innerWidth <= 800) {
      setSidebarVisible(false);
    }

    const queryObj = new Query();
    queryObj.returnGeometry = true;
    queryObj.outFields = ["*"];
    queryObj.where = `${record.field} = '${record.value}'`;

    view.closePopup();

    query.executeQueryJSON(config.parcels_url, queryObj).then(
      function (results) {
        // prints the array of result graphics to the console

        view.goTo(results.features[0]);

        view.openPopup({
          // @ts-ignore
          location: results.features[0].geometry.centroid,
          title: `Parcel: ${results.features[0].attributes.PARCEL_NO}`,
          content: `
            <table class="esri-widget__table" summary="List of attributes and values">
              <tbody>
                <tr>
                  <th style="width: 25%">Owner</th>
                  <td>${results.features[0].attributes.OwnerName}</td>
                </tr>
                <tr>
                  <th style="width: 25%">Address</th>
                  <td>${results.features[0].attributes.FullSitus}</td>
                </tr>
              </tbody>
            </table>

            <div class="ui divider" style="margin: 0.5em 0"></div>

            <table summary="List of attributes and values" style="width: 100%;">
              <tbody>
                <tr>
                  <td style="width: 50%">
                    <a href="https://desmoines.iowaassessors.com/parcel.php?parcel=${results.features[0].attributes.ParcelNumb}"} class="ui secondary tiny fluid button" style="background-color: #272727">
                      <i class="external icon"></i>
                      Assessor
                    </a>
                  </td>
                  <td style="width: 50%">
                    <a href="https://www.dmcgis.com/apps/gma?parcelid=${results.features[0].attributes.PARCEL_NO}"} class="ui secondary tiny fluid button" style="background-color: #272727">
                      <i class="external icon"></i>
                      GIS Mapping App
                    </a>
                  </td>
                </tr>
              </tbody>
            </table>
            `,
        });
      },
      function (error) {
        console.log(error); // will print error in console, if any
      }
    );
  };

  return (
    <>
      <Sidebar.Pushable
        as={Segment}
        id="site-pushable"
        attached
        style={{ height: "100%" }}
      >
        <Sidebar
          as={Segment}
          id="vanguard_sidebar"
          direction="right"
          animation="overlay"
          onHide={() => setSidebarVisible(false)}
          vertical
          visible={sidebarVisible}
          width="wide"
          style={{
            overflowY: "hidden !important",
            textAlign: "left",
            backgroundColor: "#FFFFFF",
            padding: 0,
          }}
        >
          <Menu widths={2} style={{ margin: 0 }} id="sidebar-menu">
            <Menu.Item
              name="search"
              active={selectedTab === "search"}
              onClick={() => setSelectedTab("search")}
            />
            <Menu.Item
              name="layers"
              active={selectedTab === "layers"}
              onClick={() => setSelectedTab("layers")}
            />
          </Menu>
          <div style={{ height: `${tabHeight}px`, overflowY: "auto" }}>
            {selectedTab === "search" && (
              <SearchWidget
                service={config.parcels_url}
                result_callback={searchCallback}
              />
            )}
            {selectedTab === "layers" && (
              <LayersWidget visibleLayers={visibleLayers} />
            )}
          </div>
        </Sidebar>
        <Sidebar.Pusher id="site-pusher">
          <Segment id="site-container">
            <div id="map-container">
              <div className="top-left map-buttons">
                {view && <ZoomWidget view={view} />}
              </div>
              <div className="top-right map-buttons">
                <Button onClick={() => setSidebarVisible(true)} icon>
                  <Icon name="list" />
                </Button>
              </div>
            </div>
          </Segment>
        </Sidebar.Pusher>
      </Sidebar.Pushable>
    </>
  );
};

export default Vanguard;
